import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { LeftSide, RightSide, SectionName, SubHeaderContainer, Tab, TabsDiv, ButtonCreate, RefreshIcon, RefreshIconContainer, OptionsButton } from '../../../components/styles/subheader-style'
import collectionsIcon from '../../../assets/home/shirt.svg'
import compositionIcon from '../../../assets/home/length.svg'
import funnelIcon from '../../../assets/home/production.svg'
import { TypeMainOutlet } from '..'
import refresh_act from '../../../assets/refresh-active.svg'
import filterIcon from '../../../assets/stock/TableMovementHeader/filter.svg'
import { Plus, Ellipsis, Tag, Filter as FilterIcon } from 'lucide-react'
import { ViewModeSelector } from '../Pipelines/PipelineControls'
import ProductDevelopmentFiltersMenu from './ProductDevelopmentFiltersMenu'
import { FilterDiv, FilterImg, FilterText } from './style'
import ModuleKanban from '../Pipelines/ModuleKanban'
import { FilterWrapper } from '../../../components/styles/subheader-style'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Header } from '../../../components/SubHeaderFilter/types'
import { collectionHeaders } from '../Collections'
import {
  EMPTY_PRODUCT_FILTERS,
  hasActiveProductFilters,
  type ProductDevelopmentProductFilters,
} from './productDevelopmentFilters'


export const compositionProductHeaders: Header[] = [
  { key: 'productCode', label: 'Código', type: 'string' },
  { key: 'name', label: 'Produto', type: 'string' },
  { key: 'colecao', label: 'Coleção', type: 'string' },
  { key: 'familia', label: 'Família', type: 'string' },
  { key: 'episodio', label: 'Episódio', type: 'string' },
  { key: 'grades', label: 'Grades', type: 'string' },
  { key: 'groups', label: 'Grupos', type: 'string' },
  { key: 'colors', label: 'Cores', type: 'string' }
];

const COMPOSITION_VIEW_MODE_STORAGE_KEY = 'product-development-composition-view-mode'
type CompositionViewMode = 'board' | 'table'

const tabs = [
  { path: '/panel/collection', image: collectionsIcon, name: 'Coleção' },
  { path: '/panel/composition-product', image: compositionIcon, name: 'Ficha técnica de produto' },
  { path: '/panel/collection/funnel', image: funnelIcon, name: 'Funil' },
]

const ProductDevelopmentContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const outletContext = useOutletContext<TypeMainOutlet>()
  const [showOptions, setShowOptions] = useState<boolean>(false)
  const optionsRef = useRef<HTMLUListElement | null>(null)
  const optionsButtonRef = useRef<HTMLDivElement | null>(null)
  const [optionsPosition, setOptionsPosition] = useState<{ top: number; right: number } | null>(null)
  
  const collectionsFiltersState = usePersistedFilters('collections')
  const [isCollectionFilterMenuOpen, setIsCollectionFilterMenuOpen] = useState(false)
  const collectionFilterButtonRef = useRef<HTMLDivElement | null>(null)

  const compositionFiltersState = usePersistedFilters('composition_product')
  const [isCompositionFilterMenuOpen, setIsCompositionFilterMenuOpen] = useState(false)
  const compositionFilterButtonRef = useRef<HTMLDivElement | null>(null)

  const [compositionViewMode, setCompositionViewMode] = useState<CompositionViewMode>(() => {
    const persistedMode = localStorage.getItem(COMPOSITION_VIEW_MODE_STORAGE_KEY)
    return persistedMode === 'board' ? 'board' : 'table'
  })

  const [showFilters, setShowFilters] = useState<boolean>(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)
  const filtersMenuRef = useRef<HTMLDivElement | null>(null)
  const [filtersPosition, setFiltersPosition] = useState<{ top: number; right: number } | null>(null)
  const [productFilters, setProductFilters] = useState<ProductDevelopmentProductFilters>(
    EMPTY_PRODUCT_FILTERS,
  )

  const isFormRoute =
    location.pathname.includes('/panel/collection/register') ||
    location.pathname.includes('/panel/collection/edit') ||
    location.pathname.includes('/panel/composition-product/create') ||
    location.pathname.includes('/panel/composition-product/edit')

  const isFunnelRoute = location.pathname.includes('/panel/collection/funnel')

  const activeTabIndex = (() => {
    if (location.pathname.includes('/panel/collection/funnel')) return 2
    if (location.pathname.startsWith('/panel/composition-product')) return 1
    return 0
  })()

  const activeTabName = activeTabIndex === 0 ? 'collection' : 'composition'

  const isCompositionListRoute =
    location.pathname.startsWith('/panel/composition-product') &&
    !location.pathname.includes('/panel/composition-product/create') &&
    !location.pathname.includes('/panel/composition-product/edit')



  const toggleOptions = () => {
    setShowOptions((previous) => {
      if (previous) {
        return false
      }

      if (optionsButtonRef.current) {
        const buttonRect = optionsButtonRef.current.getBoundingClientRect()
        const top = buttonRect.bottom + 8
        const right = Math.max(16, window.innerWidth - buttonRect.right)
        setOptionsPosition({ top, right })
      }

      return true
    })
  }

  const handleCompositionViewModeChange = (mode: CompositionViewMode) => {
    setCompositionViewMode(mode)
    localStorage.setItem(COMPOSITION_VIEW_MODE_STORAGE_KEY, mode)
    window.dispatchEvent(new CustomEvent('product-development-view-mode', { detail: { tab: 'composition', viewMode: mode } }))
  }

  const dispatchProductFilters = (filters: ProductDevelopmentProductFilters) => {
    setProductFilters(filters)
    window.dispatchEvent(
      new CustomEvent('product-development-filters', { detail: { filters } }),
    )
  }

  const toggleFilters = () => {
    setShowFilters((previous) => {
      if (previous) {
        return false
      }

      if (filterButtonRef.current) {
        const buttonRect = filterButtonRef.current.getBoundingClientRect()
        const top = buttonRect.bottom + 8
        const right = Math.max(16, window.innerWidth - buttonRect.right)
        setFiltersPosition({ top, right })
      }

      return true
    })
  }

  const handleApplyFilters = (filters: ProductDevelopmentProductFilters) => {
    dispatchProductFilters(filters)
  }

  const handleRefresh = () => {
    window.dispatchEvent(new CustomEvent('product-development-refresh', { detail: { tab: activeTabName } }))
  }

  const handleCreate = () => {
    if (activeTabName === 'collection') {
      navigate('/panel/collection/register')
      return
    }

    navigate('/panel/composition-product/create')
  }

  const createButtonLabel = activeTabName === 'collection' ? 'Nova coleção' : 'Novo produto'

  useEffect(() => {
    if (activeTabName !== 'collection') {
      setShowOptions(false)
      return
    }
  }, [activeTabName])

  useEffect(() => {
    const handleClickOutsideFilters = (event: MouseEvent) => {
      const target = event.target as Node
      if (filtersMenuRef.current?.contains(target)) {
        return
      }
      if (filterButtonRef.current?.contains(target)) {
        return
      }
      if (showFilters) {
        setShowFilters(false)
      }
    }

    const handleLayoutChange = () => {
      if (showFilters) {
        setShowFilters(false)
      }
    }

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutsideFilters)
      window.addEventListener('scroll', handleLayoutChange, true)
      window.addEventListener('resize', handleLayoutChange)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideFilters)
      window.removeEventListener('scroll', handleLayoutChange, true)
      window.removeEventListener('resize', handleLayoutChange)
    }
  }, [showFilters])

  useEffect(() => {
    const handleClickOutsideOptions = (event: MouseEvent) => {
      const target = event.target as Node
      if (optionsRef.current && optionsRef.current.contains(target)) {
        return
      }
      if (optionsButtonRef.current && optionsButtonRef.current.contains(target)) {
        return
      }
      if (showOptions) {
        setShowOptions(false)
      }
    }

    const handleLayoutChange = () => {
      if (showOptions) {
        setShowOptions(false)
      }
    }

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutsideOptions)
      window.addEventListener('scroll', handleLayoutChange, true)
      window.addEventListener('resize', handleLayoutChange)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideOptions)
      window.removeEventListener('scroll', handleLayoutChange, true)
      window.removeEventListener('resize', handleLayoutChange)
    }
  }, [showOptions])

  useEffect(() => {
    if (!isCompositionListRoute) {
      return
    }

    window.dispatchEvent(new CustomEvent('product-development-view-mode', {
      detail: { tab: 'composition', viewMode: compositionViewMode },
    }))
  }, [isCompositionListRoute, compositionViewMode])

  return (
    <>
      {!isFormRoute && !isFunnelRoute && (
        <SubHeaderContainer style={{ zIndex: 20 }}>
          <LeftSide>
            <SectionName>Desenvolvimento de Produto</SectionName>
            <TabsDiv>
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.path}
                  $isFirst={index === 0}
                  $isLast={index === tabs.length - 1}
                  $isActive={index === activeTabIndex}
                  onClick={() => {
                    if (tab.path !== location.pathname) {
                      navigate(tab.path)
                    }
                  }}
                >
                  <img src={tab.image} alt={`${tab.name}-icon`} />
                  {tab.name}
                </Tab>
              ))}
            </TabsDiv>
          </LeftSide>
          <RightSide>
            {activeTabName === 'collection' && (
              <FilterWrapper style={{ marginRight: 8 }}>
                <OptionsButton ref={collectionFilterButtonRef} onClick={() => setIsCollectionFilterMenuOpen(true)}>
                  <FilterIcon size={18} />
                </OptionsButton>
                {isCollectionFilterMenuOpen && (
                  <FilterMenu
                    headers={collectionHeaders}
                    onApplyFilter={(f) => {
                      collectionsFiltersState.handleApplyFilter(f)
                      setIsCollectionFilterMenuOpen(false)
                    }}
                    onClose={() => setIsCollectionFilterMenuOpen(false)}
                    anchorRef={collectionFilterButtonRef}
                    filters={collectionsFiltersState.appliedFilters}
                  />
                )}
              </FilterWrapper>
            )}
            {activeTabName === 'composition' && isCompositionListRoute && (
              <>
                <FilterWrapper style={{ marginRight: 8 }}>
                  <OptionsButton ref={compositionFilterButtonRef} onClick={() => setIsCompositionFilterMenuOpen(true)}>
                    <FilterIcon size={18} />
                  </OptionsButton>
                  {isCompositionFilterMenuOpen && (
                    <FilterMenu
                      headers={compositionProductHeaders}
                      onApplyFilter={(f) => {
                        compositionFiltersState.handleApplyFilter(f)
                        setIsCompositionFilterMenuOpen(false)
                      }}
                      onClose={() => setIsCompositionFilterMenuOpen(false)}
                      anchorRef={compositionFilterButtonRef}
                      filters={compositionFiltersState.appliedFilters}
                    />
                  )}
                </FilterWrapper>
                <ViewModeSelector
                  value={compositionViewMode}
                  onChange={(mode) => handleCompositionViewModeChange(mode as CompositionViewMode)}
                  boardLabel='Quadro'
                  tableLabel='Tabela'
                />
              </>
            )}
            <RefreshIconContainer onClick={handleRefresh} title='Sincronizar'>
              <RefreshIcon src={refresh_act} alt='Sincronizar' />
            </RefreshIconContainer>
            <ButtonCreate onClick={handleCreate}>
              <Plus size={18} />
              {createButtonLabel}
            </ButtonCreate>
          </RightSide>
        </SubHeaderContainer>
      )}
      {activeTabName === 'composition' && isCompositionListRoute && compositionFiltersState.appliedFilters.length > 0 && (
        <AppliedFiltersDisplay
          filters={compositionFiltersState.appliedFilters}
          onRemoveFilter={compositionFiltersState.handleRemoveFilter}
          onClearAll={compositionFiltersState.handleClearAll}
        />
      )}
      <Outlet context={{
        ...outletContext,
        collectionsFiltersState,
        compositionFiltersState,
        appliedFilters: activeTabName === 'collection'
          ? collectionsFiltersState.appliedFilters
          : compositionFiltersState.appliedFilters
      }} />
    </>


  )
}

export default ProductDevelopmentContent

export const ProductDevelopmentFunnel: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const activeTabIndex = (() => {
    if (location.pathname.includes('/panel/collection/funnel')) return 2
    if (location.pathname.startsWith('/panel/composition-product')) return 1
    return 0
  })()

  return (
    <ModuleKanban
      moduleType="product_development"
      moduleName="Desenvolvimento de Produto"
      subModule="fashion"
      headerLeftContent={
        <>
          <SectionName>Desenvolvimento de Produto</SectionName>
          <TabsDiv>
            {tabs.map((tab, index) => (
              <Tab
                key={tab.path}
                $isFirst={index === 0}
                $isLast={index === tabs.length - 1}
                $isActive={index === activeTabIndex}
                onClick={() => navigate(tab.path)}
              >
                <img src={tab.image} alt={`${tab.name}-icon`} />
                {tab.name}
              </Tab>
            ))}
          </TabsDiv>
        </>
      }
    />
  )
}
